import React from "react";
import { MoreHorizontal, ExternalLink, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type ArtistDetail } from "@/hooks/useArtistDetails";

interface ArtistDetailsTableProps {
  artistDetails: ArtistDetail[];
  onView: (artistDetail: ArtistDetail) => void;
  onDelete: (artistDetailId: string) => void;
  onStatusChange: (artistDetailId: string, status: ArtistDetail['status']) => void;
}

const getStatusColor = (status: ArtistDetail['status']) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'reviewed':
      return 'bg-blue-100 text-blue-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function ArtistDetailsTable({
  artistDetails,
  onView,
  onDelete,
  onStatusChange,
}: ArtistDetailsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Artist ID</TableHead>
            <TableHead>Artist</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-[50px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {artistDetails.map((artistDetail) => (
            <TableRow key={artistDetail.id}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-mono text-sm font-medium text-blue-600">{artistDetail.artistid}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{artistDetail.full_name}</span>
                  <span className="text-sm text-gray-500">{artistDetail.email_address}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  {artistDetail.phone_number && (
                    <div className="flex items-center gap-1 text-sm">
                      <Phone className="h-3 w-3" />
                      <span>{artistDetail.phone_number}</span>
                    </div>
                  )}
                  {artistDetail.whatsapp_number && (
                    <div className="flex items-center gap-1 text-sm">
                      <Phone className="h-3 w-3" />
                      <span>WhatsApp: {artistDetail.whatsapp_number}</span>
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="text-sm">{artistDetail.city_state}</span>
                  <span className="text-xs text-gray-500">{artistDetail.pin_code}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge className={getStatusColor(artistDetail.status)}>
                  {artistDetail.status}
                </Badge>
              </TableCell>
              <TableCell>
                <span className="text-sm text-gray-500">
                  {new Date(artistDetail.created_at).toLocaleDateString()}
                </span>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => onView(artistDetail)}>
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.open(`mailto:${artistDetail.email_address}`, '_blank')}>
                      Send Email
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(artistDetail.id)}
                      className="text-red-600"
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
